import React, { useState } from 'react';
import { Settings, Users, Fuel, Zap, Heart, Share2, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { carPath } from '../../utils/listingUrl';
import { shareCar } from '../../utils/shareCar';
import { showAppAlert } from '../../utils/appAlert';
import { buildPublicFileUrl } from '../../utils/fileUrl';

const FALLBACK_CAR_IMAGE = 'https://images.unsplash.com/photo-1555215695-3004980ad54e?auto=format&fit=crop&q=80&w=400';
const numberFormatter = new Intl.NumberFormat('en-RW');

const CarCard = ({ car, isWishlisted = false, onToggleWishlist }) => {
  const navigate = useNavigate();
  const [wishlistBusy, setWishlistBusy] = useState(false);
  // Gracefully handle different data shapes (API vs local)
  const displayTag = car.categoryName || car.tag || car.category;
  const displayType = car.fuelType || car.type || car.fuel;
  const displayPrice = car.pricePerDay || car.price;
  const rawImage = car.primaryImageUrl || car.imageUrl || car.image;
  const displayName = car.name || (car.make ? `${car.make} ${car.model}` : 'Unknown Car');
  const displayTransmission = car.transmission || 'Auto';
  const displaySeats = car.seats || 5;
  const displayRating = Number(car.averageRating || car.avgRating || 0);
  const displayReviewCount = Number(car.reviewCount || 0);
  const formattedPrice = numberFormatter.format(Number(displayPrice || 0));
  const displayImage = rawImage ? buildPublicFileUrl(rawImage) : FALLBACK_CAR_IMAGE;

  const handleBooking = (e) => {
    e.stopPropagation();
    navigate(carPath(car.id,displayName));
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    await shareCar({ carId: car.id, carName: displayName });
  };

  const handleWishlist = async (e) => {
    e.stopPropagation();
    if (!onToggleWishlist || wishlistBusy) {
      return;
    }

    try {
      setWishlistBusy(true);
      await onToggleWishlist(car);
    } catch (error) {
      showAppAlert({
        type: 'error',
        title: 'Wishlist update failed',
        message: error.message || 'We could not update your wishlist.'
      });
    } finally {
      setWishlistBusy(false);
    }
  };

  return (
    <div
      onClick={() => navigate(carPath(car.id,displayName))}
      className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow border border-gray-100 flex flex-col h-full cursor-pointer group"
    >
      <div className="relative aspect-[16/9] w-full bg-gray-100 p-4 overflow-hidden">
        <img
          src={displayImage}
          alt={displayName}
          onError={(event) => {
            event.currentTarget.src = FALLBACK_CAR_IMAGE;
          }}
          className="w-full h-full object-cover mix-blend-multiply rounded-md group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-4 left-4 flex items-center gap-2">
          {onToggleWishlist ? (
            <button
              onClick={handleWishlist}
              className={`flex h-10 w-10 items-center justify-center rounded-full border transition-colors ${
                isWishlisted
                  ? 'border-red-100 bg-red-50 text-red-600'
                  : 'border-white/80 bg-white/90 text-gray-700 hover:text-red-600'
              }`}
              aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
            >
              <Heart className={`w-4 h-4 ${isWishlisted ? 'fill-current' : ''}`} />
            </button>
          ) : null}
          <button
            onClick={handleShare}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/80 bg-white/90 text-gray-700 hover:text-brand-black transition-colors"
            aria-label="Share car"
          >
            <Share2 className="w-4 h-4" />
          </button>
        </div>
        {displayTag && (
          <span className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm text-gray-800 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
            {displayTag}
          </span>
        )}
      </div>

      <div className="p-6 flex-1 flex flex-col justify-between">
        <div>
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="text-lg font-bold text-gray-900 group-hover:text-brand-black transition-colors">{displayName}</h3>
              {displayReviewCount > 0 ? (
                <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-xs font-bold text-amber-700">
                  <Star className="w-3.5 h-3.5 fill-current" />
                  <span>{displayRating.toFixed(1)}</span>
                  <span className="text-amber-500">({displayReviewCount})</span>
                </div>
              ) : (
                <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-gray-50 px-3 py-1 text-xs font-bold text-gray-500">
                  <Star className="w-3.5 h-3.5" />
                  <span>0.0</span>
                </div>
              )}
            </div>
            <div className="text-right">
              <span className="text-lg font-extrabold">RWF {formattedPrice}</span>
              <span className="text-xs text-gray-500 font-medium">/day</span>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 mb-6 text-sm text-gray-500">
            <div className="flex items-center gap-1">
              <Settings className="w-4 h-4" />
              <span>{displayTransmission}</span>
            </div>
            <div className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              <span>{displaySeats} Seats</span>
            </div>
            <div className="flex items-center gap-1">
               {displayType === 'Electric' || displayType === 'EV' ? <Zap className="w-4 h-4" /> : <Fuel className="w-4 h-4" />}
               <span>{displayType}</span>
            </div>
          </div>
        </div>

        <button
          onClick={handleBooking}
          className="w-full bg-brand-black text-white py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors mt-auto"
        >
          Book Now
        </button>
      </div>
    </div>
  );
};

export default CarCard;
